import aviator from "~/assets/illustrations/indy-glasses-aviator.png";
import bernadette from "~/assets/illustrations/indy-glasses-bernadette.png";
import caravan from "~/assets/illustrations/indy-glasses-caravan.png";
import hex from "~/assets/illustrations/indy-glasses-hex.png";
import hook from "~/assets/illustrations/indy-glasses-hook.png";
import neo from "~/assets/illustrations/indy-glasses-neo.png";
import original from "~/assets/illustrations/indy-glasses-original.png";
import potter from "~/assets/illustrations/indy-glasses-potter.png";
import ray from "~/assets/illustrations/indy-glasses-ray.png";
import ski from "~/assets/illustrations/indy-glasses-ski.png";

/**
 * Catalog of hand-drawn "Indy glasses" a user can pick for their launcher.
 *
 * Each entry is one transparent PNG plus the geometry the launcher's reading
 * animation needs. `vbW` is the SVG viewBox width at a fixed height of 40 units
 * (so it matches the art's aspect and the float/perk/nod magnitudes stay
 * proportional). The `scan*` fields position the thinking reading-highlight:
 * `scanX` starts it ~a quarter into the left lens and `scanTravel` sweeps it to
 * ~3/4 of the right lens (the reduced-motion resting offset is derived as 55%
 * of the travel in CSS) — measured per-frame against the actual artwork (see
 * scripts in the design handoff), because every drawing puts its lenses in a
 * different place.
 *
 * Array order is the display order in the picker (curated, not alphabetical).
 */
export interface IndyGlasses {
  id: string;
  label: string;
  src: string;
  /** viewBox width at height 40 (matches the art aspect). */
  vbW: number;
  scanX: number;
  scanY1: number;
  scanY2: number;
  /** px the reading highlight travels right before flicking back. */
  scanTravel: number;
}

export const INDY_GLASSES: IndyGlasses[] = [
  {
    id: "original",
    label: "Original",
    src: original,
    vbW: 96,
    scanX: 20.1,
    scanY1: 14.7,
    scanY2: 29.5,
    scanTravel: 55.7,
  },
  {
    id: "ray",
    label: "Ray",
    src: ray,
    vbW: 112.3,
    scanX: 23.8,
    scanY1: 10.0,
    scanY2: 29.5,
    scanTravel: 64.4,
  },
  {
    id: "bernadette",
    label: "Bernie",
    src: bernadette,
    vbW: 143.3,
    scanX: 34.1,
    scanY1: 11.7,
    scanY2: 30.3,
    scanTravel: 74.8,
  },
  {
    id: "hex",
    label: "Hex",
    src: hex,
    vbW: 78.4,
    scanX: 15.0,
    scanY1: 17.2,
    scanY2: 31.7,
    scanTravel: 48.2,
  },
  {
    id: "caravan",
    label: "Caravan",
    src: caravan,
    vbW: 105.5,
    scanX: 23.6,
    scanY1: 12.5,
    scanY2: 30.6,
    scanTravel: 58.0,
  },
  {
    id: "aviator",
    label: "Aviator",
    src: aviator,
    vbW: 102.1,
    scanX: 20.7,
    scanY1: 10.9,
    scanY2: 29.1,
    scanTravel: 60.5,
  },
  {
    id: "potter",
    label: "Potter",
    src: potter,
    vbW: 97.5,
    scanX: 20.1,
    scanY1: 9.7,
    scanY2: 30.5,
    scanTravel: 57.8,
  },
  {
    id: "neo",
    label: "Neo",
    src: neo,
    vbW: 128.9,
    scanX: 25.8,
    scanY1: 12.4,
    scanY2: 27.6,
    scanTravel: 74.7,
  },
  {
    id: "ski",
    label: "Ski",
    src: ski,
    vbW: 79.3,
    scanX: 15.9,
    scanY1: 12.2,
    scanY2: 27.6,
    scanTravel: 46.0,
  },
  {
    id: "hook",
    label: "Hook",
    src: hook,
    vbW: 64.6,
    scanX: 12.9,
    scanY1: 12.3,
    scanY2: 27.6,
    scanTravel: 37.5,
  },
];

/** Resolve a stored id to its entry, falling back to the default. */
export function glassesById(id: string | null | undefined): IndyGlasses {
  return INDY_GLASSES.find((g) => g.id === id) ?? INDY_GLASSES[0];
}
